import { useOutletContext, useSearchParams } from 'react-router-dom'

import { Box, Button, Card, Tag, Text } from '@factorialco/deprecated-design-system'
import { PerformanceReviewOverviewPendingAgreementsTaskDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { MyAgreements } from 'modules/performance_process/components/overview/myAgreements'
import { TaskCard } from 'modules/performance_process/components/review/show/Tasks/TaskCard'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

export const PendingAgreementsTask = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSidepanelOpen = searchParams.get('myAgreementsPanel') === 'open'
  const context = useOutletContext<ReviewOverviewOutletContext>()

  const { data, loading } = useQuery(PerformanceReviewOverviewPendingAgreementsTaskDocument, {
    variables: {
      id: context.reviewProcess.id,
    },
  })

  const reviewProcess = data?.performance.reviewProcess

  if (loading || !reviewProcess) return null

  const targetsCount = data?.performance.targets.totalCount ?? 0
  const isVisible =
    reviewProcess.agreementsConfiguration.enabled &&
    reviewProcess.reviewerStrategies?.includes('manager') &&
    targetsCount > 0

  if (!isVisible) return null

  const targetsWithoutAgreementCount = data?.performance.targetsWithoutAgreement.totalCount ?? 0
  const targetsWithInitiableAgreementCount =
    data?.performance.targetsWithInitiableAgreement.totalCount ?? 0
  const targetsWithSignedAgreementCount =
    data?.performance.targetsWithSignedAgreement.totalCount ?? 0
  const targetsWithEsignedAgreementCount =
    data?.performance.targetsWithEsignedAgreement.totalCount ?? 0

  const targetsWithFinishedAgreementCount =
    targetsWithSignedAgreementCount + targetsWithEsignedAgreementCount

  const isDisabled =
    targetsWithoutAgreementCount === targetsCount && targetsWithInitiableAgreementCount === 0
  const isCompleted =
    targetsWithSignedAgreementCount + targetsWithEsignedAgreementCount === targetsCount

  const pendingCount =
    targetsCount -
    targetsWithFinishedAgreementCount -
    targetsWithoutAgreementCount +
    targetsWithInitiableAgreementCount

  const onClick = () => setSearchParams({ myAgreementsPanel: 'open' })

  return (
    <>
      <TaskCard onClick={isDisabled ? undefined : onClick}>
        <Card.Title
          size="small"
          action={
            <>
              {!isCompleted && pendingCount > 0 && (
                <Tag leftItem="circle" inline capitalize type="tangerine">
                  {i18n.t('performance_process.overview.tasks.pending_tasks', {
                    count: pendingCount,
                  })}
                </Tag>
              )}
              {!isCompleted && isDisabled && (
                <Tag leftItem="circle" inline capitalize type="grey">
                  {i18n.t('performance_process.overview.tasks.agreements.not_available_yet')}
                </Tag>
              )}
            </>
          }
        >
          {i18n.t('performance_process.overview.tasks.agreements.title')}
        </Card.Title>
        <Card.Content>
          <Box gap="s16" width="full" flexGrow justifyContent="spaceBetween">
            <Text.Normal color="grey1000">
              {i18n.t('performance_process.overview.tasks.agreements.description')}
            </Text.Normal>
            {!isDisabled && (
              <Button onClick={onClick} type="secondary">
                {pendingCount === 0
                  ? i18n.t('performance_process.overview.tasks.view_all')
                  : i18n.t('performance_process.overview.tasks.agreements.action')}
              </Button>
            )}
          </Box>
        </Card.Content>
      </TaskCard>

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wideXL"
        title={i18n.t('performance_process.agreements.my_agreements.title_new')}
      >
        <MyAgreements
          republishEvaluationsEnabled={
            reviewProcess.republishEvaluationsConfiguration?.enabled ?? false
          }
        />
      </Sidepanel>
    </>
  )
}
